<template>
    <v-container fluid>
      <v-card class="mx-auto" max-width="600">
        <v-card-title class="text-h5 font-weight-bold">
          Estado de Conexión
          <v-spacer></v-spacer>
          <v-btn icon @click="refreshQR" :loading="restarting">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="text-center">
          <!-- Loading State -->
          <div v-if="loading || restarting" class="d-flex justify-center align-center" style="height: 400px">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>

          <!-- Error State -->
          <div v-else-if="error && !isWorking" class="d-flex justify-center align-center" style="height: 400px">
            <v-alert type="error" outlined>
              {{ error }}
              <v-btn text color="error" @click="refreshQR" :loading="restarting">
                Reintentar
              </v-btn>
            </v-alert>
          </div>

          <!-- Working State -->
          <div v-else-if="isWorking" class="d-flex flex-column justify-center align-center" style="height: 400px">
            <v-icon
              color="success"
              size="120"
              class="mb-4"
            >
              mdi-check-circle
            </v-icon>
            <span class="text-h5 success--text font-weight-medium">Sincronización Activa</span>
            <div v-if="connectedNumber" class="mt-4">
              <v-chip
                color="primary"
                outlined
                large
              >
                <v-icon left>mdi-cellphone</v-icon>
                {{ formatPhoneNumber(connectedNumber) }}
              </v-chip>
            </div>
          </div>

          <!-- QR Code State -->
          <div v-else>
            <img
              :src="qrImageBlob"
              alt="QR Code"
              class="qr-image ma-4"
              v-if="qrImageBlob"
            />
            <v-alert
              v-if="branchInfo"
              type="info"
              outlined
              class="mt-4"
            >
              Conectando a: {{ branchInfo.branchName }}
            </v-alert>
          </div>
        </v-card-text>
      </v-card>
    </v-container>
  </template>

  <script>
  import { getAuth } from "firebase/auth";
  import { firebaseApp } from "../firebase";
  import { getFirestore, doc, getDoc } from "firebase/firestore";
  import axios from 'axios';
  import { API_CONFIG } from '../config/api';

  const db = getFirestore(firebaseApp);

  export default {
    name: 'WSServerComponent',

    data: () => ({
      qrImageBlob: null,
      loading: true,
      error: null,
      branchInfo: null,
      currentBranch: null,
      isWorking: false,
      connectedNumber: null,
      restarting: false
    }),

    computed: {
      selectedBranch() {
        return this.$store.state.selectedBranch;
      }
    },

    watch: {
      selectedBranch: {
        immediate: true,
        handler(newValue) {
          this.currentBranch = newValue;
          if (this.currentBranch) {
            this.fetchQRCode();
          }
        }
      }
    },

    methods: {
      formatPhoneNumber(number) {
        // Remove non-digit characters and trim the "+1" if present
        const cleaned = number.replace(/\D+/g, '').replace(/^1/, '');
        // Format as (XXX) XXX-XXXX
        return `+1 (${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`;
      },

      async restartSession() {
        try {
          this.restarting = true;
          this.error = null;

          const response = await axios({
            method: 'post',
            url: `${API_CONFIG.WHATSAPP_SERVER_URL}/api/sessions/${this.currentBranch}/restart`,
            headers: {
              'Accept': 'application/json',
              'X-Api-Key': API_CONFIG.API_KEY
            }
          });

          console.log('Sesión reiniciada:', response.data);

          // Esperar un momento antes de obtener el QR
          await new Promise(resolve => setTimeout(resolve, 2000));

          // Obtener el nuevo QR después de reiniciar
          await this.fetchQRCode();

        } catch (error) {
          console.error('Error reiniciando sesión:', error);
          this.error = 'Error al reiniciar la sesión. Por favor, intente nuevamente.';
          this.loading = false;
        } finally {
          this.restarting = false;
        }
      },

      async fetchSessionInfo() {
        try {
          const response = await axios({
            method: 'get',
            url: `${API_CONFIG.WHATSAPP_SERVER_URL}/api/sessions/${this.currentBranch}`,
            headers: {
              'Accept': 'application/json',
              'X-Api-Key': API_CONFIG.API_KEY
            }
          });

          if (response.data && response.data.me && response.data.me.id) {
            this.connectedNumber = response.data.me.id;
          }
        } catch (error) {
          console.error('Error fetching session info:', error);
        }
      },

      async fetchQRCode() {
        this.loading = true;
        this.error = null;
        this.isWorking = false;
        this.qrImageBlob = null;
        this.connectedNumber = null;

        try {
          // Obtener información de la sucursal
          const branchRef = doc(db, "branches", this.currentBranch);
          const branchSnap = await getDoc(branchRef);

          if (!branchSnap.exists()) {
            throw new Error("No se encontró la información de la sucursal");
          }

          this.branchInfo = branchSnap.data();

          // Hacer request del QR con axios
          const response = await axios({
            method: 'get',
            url: `${API_CONFIG.WHATSAPP_SERVER_URL}/api/${this.currentBranch}/auth/qr?format=image`,
            headers: {
              'Accept': 'image/png',
              'X-Api-Key': API_CONFIG.API_KEY
            },
            responseType: 'blob'
          });

          // Crear URL del blob
          const blob = new Blob([response.data], { type: 'image/png' });
          this.qrImageBlob = URL.createObjectURL(blob);

        } catch (error) {
          console.error('Error fetching QR code:', error);

          // Verificar si es error 422 con status WORKING
          if (error.response && error.response.status === 422) {
            try {
              const errorData = await error.response.data.text();
              const parsedError = JSON.parse(errorData);
              if (parsedError.status === "WORKING") {
                this.isWorking = true;
                this.error = null;
                // Obtener información de la sesión
                await this.fetchSessionInfo();
                return;
              }
            } catch (parseError) {
              console.error('Error parsing error response:', parseError);
            }
          }

          this.error = 'Error al cargar el código QR. Por favor, intente nuevamente.';
        } finally {
          this.loading = false;
        }
      },

      async refreshQR() {
        // Reiniciar la sesión en el backend antes de obtener el QR
        await this.restartSession();
      }
    },

    beforeDestroy() {
      // Limpiar URLs de blob al destruir el componente
      if (this.qrImageBlob) {
        URL.revokeObjectURL(this.qrImageBlob);
      }
    }
  };
  </script>

  <style scoped>
  .qr-image {
    max-width: 300px;
    width: 100%;
    height: auto;
    border-radius: 8px;
  }

  .v-card {
    border-radius: 12px;
    transition: transform 0.2s;
  }

  .v-card:hover {
    transform: translateY(-2px);
  }
  </style>
